<script setup lang="ts">
/**DataTableToolbar.vue - Não aplica filtro inicial se já houver filtros */
import type {Table} from '@tanstack/vue-table';
import type {FlatCampaign} from './flattenCampaigns';
import { computed, onMounted, ref, nextTick, watch, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';

// Componentes shadcn
import {Button} from 'dashboard/components-shadcn/new-york/ui/button';
import {Input} from 'dashboard/components-shadcn/new-york/ui/input';

import DataTableFacetedFilter from './DataTableFacetedFilter.vue';
import DataTableFacetedGroup from './DataTableFacetedGroup.vue';
import DataTableViewOptions from './DataTableViewOptions.vue';

interface DataTableToolbarProps {
  table: Table<FlatCampaign>;
  rowGrouping: string[];
  tableStats?: any;
  isProcessing?: boolean;
  campaignStatusValues: { value: string; label: string }[];
  serverSidePagination?: boolean; // Quando true, o componente pai controla o filtro inicial
  initialDateFilter?: { startDate: string; endDate: string } | null; // Filtro de data inicial (server-side)
  isLoading?: boolean; // Estado de carregamento do servidor
}

const props = withDefaults(defineProps<DataTableToolbarProps>(), {
  isProcessing: false,
  serverSidePagination: false,
  initialDateFilter: null,
  isLoading: false,
});

const emit = defineEmits<{
  expandAll: []
  collapseAll: []
  expandFirstLevel: []
  dateRangeChange: [value: Date[]]
}>();

const route = useRoute();

// ============================================
// ESTADO DO COMPONENTE
// ============================================
const customDateRange = ref<Date[]>([]);
const datePickerRef = ref<HTMLElement>();
const showDatePicker = ref(false);
const hasAppliedInitialFilter = ref(false);

// ✅ NOVO: Controle de navegação
const previousRoute = ref<string>('');
const isReturningFromBroadcastList = ref(false);

// Lista de rotas que não devem resetar o filtro ao retornar
const CHILD_ROUTES = [
  'campaigns_whatsapp_official_schedule_deliveries',
];

// ============================================
// FUNÇÕES AUXILIARES
// ============================================
function getCurrentDateRange(): Date[] {
  const today = new Date();
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);
  
  const endOfDay = new Date(today);
  endOfDay.setHours(23, 59, 59, 999);
  
  return [startOfDay, endOfDay];
}

function formatDateRange(dateRange: any): string {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
    return '';
  }

  const start = new Date(dateRange[0]);
  const end = new Date(dateRange[1]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return `${formatDate(start)} - ${formatDate(end)}`;
}

// ✅ NOVO: Verificar se há QUALQUER filtro já aplicado na tabela
function hasExistingFilters(): boolean {
  // Verificar filtros de coluna
  const hasColumnFilters = props.table.getState().columnFilters.length > 0;
  
  // Verificar filtro global
  const hasGlobalFilter = !!props.table.getState().globalFilter;
  
  // Verificar agrupamento
  const hasGrouping = props.rowGrouping.length > 0;
  
  return hasColumnFilters || hasGlobalFilter || hasGrouping;
}

function isComingFromChildRoute(previousRouteName: string): boolean {
  return CHILD_ROUTES.some(childRoute => previousRouteName.includes(childRoute));
}

// ============================================
// COMPUTED PROPERTIES
// ============================================
const datePickerStyle = computed(() => {
  if (!datePickerRef.value) return {};
  
  const rect = datePickerRef.value.getBoundingClientRect();
  return {
    top: `${rect.bottom + 8}px`,
    left: `${rect.left}px`,
    minWidth: '300px'
  };
});

// ✅ Computed para obter o filtro de data atual (server-side ou client-side)
const currentDateFilter = computed(() => {
  // Se server-side e há customDateRange definido, usar esse
  if (props.serverSidePagination && customDateRange.value.length === 2) {
    return customDateRange.value;
  }
  // Caso contrário, usar o filtro do TanStack Table
  return props.table.getColumn('scheduled_at')?.getFilterValue() as Date[] | undefined;
});

const isDefaultDateFilter = computed(() => {
  const filter = currentDateFilter.value;
  if (!filter || !Array.isArray(filter) || filter.length !== 2) {
    return false;
  }

  const defaultRange = getCurrentDateRange();
  const currentStart = new Date(filter[0]);
  const currentEnd = new Date(filter[1]);

  // Comparar apenas data (ignorar horas para determinar se é "hoje")
  const isSameDay = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  return isSameDay(currentStart, defaultRange[0]) && isSameDay(currentEnd, defaultRange[1]);
});

const dateButtonText = computed(() => {
  const filter = currentDateFilter.value;

  if (!filter || !Array.isArray(filter) || filter.length !== 2) {
    return 'Selecionar período';
  }

  if (isDefaultDateFilter.value) {
    return 'Hoje';
  }

  return formatDateRange(filter);
});

const groupableColumns = computed(() => {
  return props.table.getAllColumns().filter(col => col.getCanGroup());
});

const isFiltered = computed(() => {
  const hasNonDateFilters = props.table.getState().columnFilters.some(filter => filter.id !== 'scheduled_at');
  const hasDateFilter = props.table.getColumn('scheduled_at')?.getFilterValue();
  const hasGlobalFilter = props.table.getState().globalFilter;
  // Em server-side mode, verificar customDateRange também
  const hasServerSideDateFilter = props.serverSidePagination && customDateRange.value.length === 2;

  return hasNonDateFilters || hasDateFilter || hasGlobalFilter || hasServerSideDateFilter;
});

const hasGrouping = computed(() => props.rowGrouping.length > 0);

// ============================================
// HANDLERS DE FILTRO DE DATA
// ============================================
function onDateRangeChange(value: Date[]) {
  customDateRange.value = value;

  if (value && value.length === 2) {
    const startDate = new Date(value[0]);
    const endDate = new Date(value[1]);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Em server-side mode, NÃO usar setFilterValue do TanStack Table
    // O evento dateRangeChange vai propagar para o componente pai que fará o fetch
    if (!props.serverSidePagination) {
      props.table.getColumn('scheduled_at')?.setFilterValue([startDate, endDate]);
    }
  } else {
    if (!props.serverSidePagination) {
      props.table.getColumn('scheduled_at')?.setFilterValue(undefined);
    }
  }

  emit('dateRangeChange', value);
}

function onDateRangeChangeAndClose(value: Date[]) {
  onDateRangeChange(value);
  showDatePicker.value = false;
}

function clearDateFilter() {
  if (props.serverSidePagination) {
    customDateRange.value = [];
    emit('dateRangeChange', []);
  } else {
    props.table.getColumn('scheduled_at')?.setFilterValue(undefined);
    customDateRange.value = getCurrentDateRange();
  }
}

// ============================================
// APLICAÇÃO DO FILTRO INICIAL
// ============================================

function applyInitialDateFilter() {
  if (hasAppliedInitialFilter.value) {
    return;
  }

  // Se server-side pagination, o componente pai controla o filtro inicial
  if (props.serverSidePagination) {
    hasAppliedInitialFilter.value = true;
    return;
  }

  // Se está retornando de uma rota filha, não aplicar filtro
  if (isReturningFromBroadcastList.value) {
    isReturningFromBroadcastList.value = false;
    return;
  }

  // Se já houver qualquer filtro aplicado, não aplicar filtro de data
  if (hasExistingFilters()) {
    hasAppliedInitialFilter.value = true;
    return;
  }

  // Verificar se há dados na tabela
  const hasData = props.table.getCoreRowModel().rows.length > 0;
  if (!hasData) {
    return;
  }

  const defaultRange = getCurrentDateRange();
  customDateRange.value = defaultRange;
  onDateRangeChange(defaultRange);

  // Marcar como aplicado
  hasAppliedInitialFilter.value = true;
}

// ============================================
// SETUP INICIAL DO COMPONENTE
// ============================================
function setupComponent() {
  nextTick(() => {
    // Adicionar CSS fix para date picker (apenas uma vez)
    if (!document.getElementById('woot-datepicker-fix')) {
      const style = document.createElement('style');
      style.id = 'woot-datepicker-fix';
      style.textContent = `
        .woot-date-picker [class*="absolute"][class*="top-9"] {
          position: absolute !important;
          top: calc(100% + 8px) !important;
          left: 0 !important;
          z-index: 9999 !important;
          transform: none !important;
        }
        .table-wrapper {
          overflow-x: auto !important;
          overflow-y: visible !important;
        }
      `;
      document.head.appendChild(style);
    }

    // Se server-side e há filtro inicial, inicializar customDateRange para UI
    if (props.serverSidePagination && props.initialDateFilter) {
      const { startDate, endDate } = props.initialDateFilter;
      customDateRange.value = [new Date(startDate), new Date(endDate)];
    }

    // Aplicar filtro inicial se necessário
    applyInitialDateFilter();
  });
}

// ============================================
// LIFECYCLE HOOKS
// ============================================
onMounted(() => {
  previousRoute.value = route.name as string || route.path;
  setupComponent();
});

onBeforeUnmount(() => {
  showDatePicker.value = false;
});

// ============================================
// WATCHERS
// ============================================

// Watch para sincronizar customDateRange quando initialDateFilter muda (server-side mode)
watch(() => props.initialDateFilter, (newFilter) => {
  if (!props.serverSidePagination) return;

  if (newFilter) {
    const { startDate, endDate } = newFilter;
    customDateRange.value = [new Date(startDate), new Date(endDate)];
  } else {
    customDateRange.value = [];
  }
}, { deep: true });

// Watch inteligente para mudanças de rota
watch(() => route.name, (newRouteName, oldRouteName) => {
  if (!newRouteName || !oldRouteName) return;
  
  const newName = newRouteName as string;
  const oldName = oldRouteName as string;
  
  // Se está saindo da tabela para uma rota filha
  if (CHILD_ROUTES.some(childRoute => newName.includes(childRoute))) {
    previousRoute.value = oldName;
    return;
  }
  
  // Se está voltando de uma rota filha
  if (isComingFromChildRoute(oldName)) {
    isReturningFromBroadcastList.value = true;
    previousRoute.value = newName;
    return;
  }
  
  // Se mudou para uma rota completamente diferente (não é filho)
  if (oldName !== newName) {
    // Reset completo apenas quando muda para rota diferente (não filho)
    hasAppliedInitialFilter.value = false;
    isReturningFromBroadcastList.value = false;
    customDateRange.value = getCurrentDateRange();
    props.table.getColumn('scheduled_at')?.setFilterValue(undefined);
    previousRoute.value = newName;
    
    nextTick(() => {
      setupComponent();
    });
  }
});
</script>

<template>
  <div class="space-y-4">
    <!-- Toolbar Principal -->
    <div class="flex items-center justify-between">
      <div class="flex flex-1 items-center space-x-2 mt-2">
        <!-- Campo de busca global -->
        <Input
          placeholder="Filtrar em todas as colunas..."
          :model-value="table.getState().globalFilter ?? ''"
          class="h-8 w-[150px] lg:w-[250px]"
          @input="table.setGlobalFilter($event.target.value)"
        />

        <!-- Loading indicator -->
        <div v-if="isLoading" class="flex items-center gap-2 text-xs text-n-slate-11">
          <span class="i-lucide-loader-2 w-4 h-4 animate-spin"></span>
          <span>Carregando...</span>
        </div>

        <!-- Date Picker -->
        <div
          class="date-picker-container relative
                hover:border-n-slate-7
                bg-n-slate-4 hover:bg-n-slate-2
                dark:bg-slate-800 dark:hover:bg-woot-900
                dark:hover:border-slate-600
                rounded-md"
          ref="datePickerRef">
          <Button
            variant="outline"
            size="sm"
            :disabled="isLoading"
            @click="showDatePicker = !showDatePicker"
            :class="[
              'h-8 border-b border-n-slate-6 bg-n-slate-6 hover:bg-n-slate-4 dark:hover:bg-slate-4 transition-colors',
              {
                'bg-green-100 border-green-300 text-green-800 hover:bg-green-200 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300': !isDefaultDateFilter && currentDateFilter?.length === 2,
                'bg-woot-100 border-woot-300 text-woot-800 hover:bg-woot-200 dark:bg-woot-900/30 dark:border-woot-700 dark:text-woot-300': isDefaultDateFilter,
                'opacity-50 cursor-not-allowed': isLoading
              }
            ]"
          >
            <span class="i-lucide-calendar w-4 h-4 mr-2"></span>
            {{ dateButtonText }}
          </Button>
          
          <!-- Date picker renderizado no body -->
          <Teleport to="body" v-if="showDatePicker">
            <div 
              class="fixed inset-0 z-[9999]"
              @click="showDatePicker = false"
            >
              <div
                :style="datePickerStyle"
                class="absolute bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 rounded-lg p-4"
                @click.stop
              >
                <woot-date-picker 
                  @date-range-changed="onDateRangeChangeAndClose"
                  :modelValue="customDateRange"
                />
              </div>
            </div>
          </Teleport>
        </div>

        <!-- Filtro de Status -->
        <DataTableFacetedFilter
          v-if="table.getColumn('campaign_status')"
          :column="table.getColumn('campaign_status')"
          title="Status da Campanha"
          :options="campaignStatusValues"
          :disabled="isLoading"
        />

        <!-- Controle de Agrupamento -->
        <DataTableFacetedGroup
          v-if="groupableColumns.length > 0"
          :table="table"
          :groupableColumns="groupableColumns"
          :rowGrouping="rowGrouping"
          :disabled="isLoading"
        />
      </div>

      <!-- Opções de visualização -->
      <DataTableViewOptions :table="table" />
    </div>

    <!-- Barra de Status e Controles de Agrupamento -->
    <div v-if="hasGrouping" class="flex items-center justify-between bg-slate-50 dark:bg-green-900/10 p-1 px-2 border border-slate-200 dark:border-green-700 rounded-md">
      <!-- Estatísticas da Tabela -->
      <div class="flex items-center space-x-4 text-sm text-slate-600 dark:text-slate-400">
        <div v-if="tableStats" class="flex items-center space-x-4">
          <!-- Grupos -->
          <div v-if="tableStats.hasGrouping" class="flex items-center gap-1">
            <span class="i-lucide-folder w-4 h-4"></span>
            <span>{{ tableStats.groupedRows }} grupo(s)</span>
          </div>

          <!-- Selecionados -->
          <div v-if="tableStats.selectedRows > 0" class="flex items-center gap-1 text-slate-700 dark:text-slate-400">
            <span class="i-lucide-check-square w-4 h-4"></span>
            <span>{{ tableStats.selectedRows }} selecionado(s)</span>
          </div>

          <!-- Indicador de processamento -->
          <div v-if="isProcessing" class="flex items-center gap-1 text-slate-700 dark:text-slate-400">
            <div class="w-3 h-3 border border-slate-500 border-t-transparent rounded-full animate-spin"></div>
            <span class="text-xs">Processando...</span>
          </div>
        </div>
      </div>

      <!-- Controles de Expansão -->
      <div v-if="hasGrouping" class="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          @click="emit('expandFirstLevel')"
          :disabled="isProcessing"
          class="h-7 px-2 text-xs"
          title="Expandir primeiro nível"
        >
          <span class="i-lucide-chevron-right w-3 h-3 mr-1"></span>
          1º Nível
        </Button>

        <Button
          variant="outline"
          size="sm"
          @click="emit('expandAll')"
          :disabled="isProcessing"
          class="h-7 px-2 text-xs"
          title="Expandir todos os grupos"
        >
          <span class="i-lucide-chevrons-down w-3 h-3 mr-1"></span>
          Expandir Todos
        </Button>

        <Button
          variant="outline"
          size="sm"
          @click="emit('collapseAll')"
          :disabled="isProcessing"
          class="h-7 px-2 text-xs"
          title="Colapsar todos os grupos"
        >
          <span class="i-lucide-chevrons-up w-3 h-3 mr-1"></span>
          Colapsar Todos
        </Button>
      </div>
    </div>

    <!-- Filtros Ativos -->
    <div v-if="isFiltered" class="flex flex-wrap items-center gap-2">
      <span class="text-xs text-slate-500 dark:text-slate-400">Filtros ativos:</span>

      <!-- Filtro global -->
      <div
        v-if="table.getState().globalFilter"
        class="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-900/20 text-slate-700 dark:text-slate-300 text-xs rounded border border-slate-200 dark:border-slate-700"
      >
        <span class="i-lucide-search w-3 h-3"></span>
        <span>Busca global: {{ table.getState().globalFilter }}</span>
        <button
          @click="table.setGlobalFilter('')"
          class="hover:bg-slate-100 dark:hover:bg-slate-800 rounded p-0.5 transition-colors"
        >
          <span class="i-lucide-x w-3 h-3"></span>
        </button>
      </div>

      <!-- Filtro de data ativo -->
      <div
        v-if="currentDateFilter && currentDateFilter.length === 2"
        class="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 dark:bg-slate-900/20 text-slate-700 dark:text-slate-300 text-xs rounded border border-slate-200 dark:border-slate-700"
      >
        <span class="i-lucide-calendar w-3 h-3"></span>
        <span v-if="isDefaultDateFilter">Data: Hoje</span>
        <span v-else>Data: {{ formatDateRange(currentDateFilter) }}</span>
        <button
          @click="clearDateFilter"
          class="hover:bg-slate-100 dark:hover:bg-slate-800 rounded p-0.5 transition-colors"
        >
          <span class="i-lucide-x w-3 h-3"></span>
        </button>
      </div>

      <!-- Filtro de status ativo -->
      <div
        v-if="table.getColumn('campaign_status')?.getFilterValue()?.length"
        class="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-xs rounded border border-green-200 dark:border-green-700"
      >
        <span class="i-lucide-filter w-3 h-3"></span>
        <span>Status: {{ (table.getColumn('campaign_status')?.getFilterValue() as string[])?.map(v => campaignStatusValues.find(s => s.value === v)?.label || v).join(', ') }}</span>
        <button
          @click="table.getColumn('campaign_status')?.setFilterValue(undefined)"
          class="hover:bg-green-100 dark:hover:bg-green-800 rounded p-0.5 transition-colors"
        >
          <span class="i-lucide-x w-3 h-3"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.date-picker-container {
  position: relative;
}

:deep(.text-primary) {
  @apply text-slate-700 dark:text-slate-400;
}

:deep(.border-primary) {
  @apply border-slate-500;
}

:deep(.ring-primary) {
  @apply ring-slate-500;
}

:deep(.bg-primary) {
  @apply bg-slate-500;
}

:deep(input:focus) {
  @apply ring-2 ring-slate-500 border-slate-500;
}

:deep(button:focus-visible) {
  @apply ring-2 ring-slate-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900;
}

:deep(button:hover) {
  @apply transition-all duration-200;
}

:deep(button:disabled) {
  @apply opacity-50 cursor-not-allowed;
}

:deep(input) {
  @apply border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-500 dark:placeholder:text-slate-400;
}

@media (max-width: 768px) {
  .toolbar-container {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .toolbar-main {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .toolbar-input {
    width: 100%;
    min-width: 200px;
  }

  .status-bar {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .status-stats {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .group-controls {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-active,
.fade-leave-to {
  opacity: 0;
}
</style>